import { useTranslation } from "react-i18next"
import { cn } from "@f2e/ui/lib/utils"

interface DragDropOverlayProps {
  isVisible: boolean
  className?: string
  children?: React.ReactNode
  /** When true, hides the icon and text (useful for small drop zones) */
  minimal?: boolean
}

/**
 * Reusable drag-and-drop overlay component
 * Provides consistent UI feedback when files are being dragged over a drop zone
 */
export const DragDropOverlay = ({
  isVisible,
  className,
  children,
  minimal = false,
}: DragDropOverlayProps) => {
  const { t } = useTranslation()

  if (!isVisible) return null

  return (
    <div
      className={cn(
        "absolute inset-0 rounded-lg border-2 border-dashed border-primary bg-primary/5 dark:bg-primary/10 backdrop-blur-xs z-50 pointer-events-none",
        className
      )}
    >
      {!minimal && (
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/20 dark:bg-primary/30 flex items-center justify-center">
              <svg
                className="w-8 h-8 text-primary"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
            </div>
            <p className="text-primary font-medium text-lg">
              {t("common.fileUpload.dragOverlay.dropFilesHere")}
            </p>
            <p className="text-primary/70 text-sm mt-1">
              {t("common.fileUpload.dragOverlay.releaseToUpload")}
            </p>
          </div>
        </div>
      )}
      {children}
    </div>
  )
}

export default DragDropOverlay
